(() => {
  'use strict';

  if (window.RDVariantV2?.ready) return;

  const API =
    window.RDVariantV2 =
      window.RDVariantV2 || {};

  API.ready = true;
  API.version = '2.4.2-manual-choice';

  const PICKER_SELECTOR =
    'variant-selects[data-rd-variant-v2]';

  const FIELD_SELECTOR =
    '[data-rd-v2-field]';

  const CONTROL_SELECTOR =
    '[data-rd-v2-control]';

  const VARIANT_MAP_SELECTOR =
    '[data-rd-v2-variant-map]';

  const states = new Map();
  const fieldPools = new WeakMap();
  const pendingFrames = new WeakMap();


  /* ==========================================================
     BASIC HELPERS
     ========================================================== */

  const getPicker = (element) =>
    element?.closest?.(
      PICKER_SELECTOR
    );

  const getStateKey = (picker) =>
    `${
      picker?.dataset?.section ||
      picker?.id ||
      ''
    }:${
      picker?.dataset?.productId ||
      ''
    }`;

  const isCascade = (picker) =>
    picker?.dataset?.rdV2Cascade ===
    'true';

  const getFields = (picker) =>
    Array.from(
      picker?.querySelectorAll(
        FIELD_SELECTOR
      ) || []
    ).sort(
      (a, b) =>
        Number(
          a.dataset.rdV2Stage || 0
        ) -
        Number(
          b.dataset.rdV2Stage || 0
        )
    );

  const getControls = (field) =>
    Array.from(
      field?.querySelectorAll(
        CONTROL_SELECTOR
      ) || []
    );

  const getSelect = (field) =>
    field?.querySelector(
      'select[data-rd-v2-control]'
    );

  const getRadios = (field) =>
    Array.from(
      field?.querySelectorAll(
        'input[type="radio"][data-rd-v2-control]'
      ) || []
    );


  /* ==========================================================
     VARIANT MATRIX
     ========================================================== */

  const parseVariantMap = (picker) => {
    const script =
      picker?.querySelector(
        VARIANT_MAP_SELECTOR
      );

    if (!script) return [];

    try {
      const parsed =
        JSON.parse(
          script.textContent || '[]'
        );

      if (!Array.isArray(parsed)) {
        return [];
      }

      return parsed
        .filter(
          (variant) =>
            variant &&
            Array.isArray(
              variant.options
            )
        )
        .map((variant) => ({
          id: String(
            variant.id || ''
          ),

          available:
            variant.available !== false,

          options:
            variant.options.map(
              (value) =>
                String(
                  value ?? ''
                )
            )
        }));
    } catch (error) {
      console.error(
        '[RD V2] Unable to parse variant map.',
        error
      );

      return [];
    }
  };


  /* ==========================================================
     STORE ORIGINAL OPTIONS
     ========================================================== */

  const ensureFieldPool = (field) => {
    if (!field) return null;

    if (
      fieldPools.has(field)
    ) {
      return fieldPools.get(
        field
      );
    }

    const select =
      getSelect(field);

    if (select) {
      const placeholder =
        select.querySelector(
          'option[data-rd-v2-placeholder]'
        );

      const pool = {
        type: 'select',

        placeholder:
          placeholder
            ? placeholder.cloneNode(
                true
              )
            : null,

        options:
          Array.from(
            select.options
          )
            .filter(
              (option) =>
                !option.hasAttribute(
                  'data-rd-v2-placeholder'
                )
            )
            .map(
              (option) =>
                option.cloneNode(
                  true
                )
            )
      };

      fieldPools.set(
        field,
        pool
      );

      return pool;
    }

    const pool = {
      type: 'radio'
    };

    fieldPools.set(
      field,
      pool
    );

    return pool;
  };


  /* ==========================================================
     COMPATIBLE VALUES
     ========================================================== */

  const getAllowedValues = (
    variants,
    selections,
    stage
  ) => {
    const values =
      new Set();

    if (stage > 0) {
      for (
        let index = 0;
        index < stage;
        index += 1
      ) {
        if (
          !selections[index]
        ) {
          return values;
        }
      }
    }

    variants.forEach(
      (variant) => {
        let matches = true;

        for (
          let index = 0;
          index < stage;
          index += 1
        ) {
          if (
            variant.options[index] !==
            selections[index]
          ) {
            matches = false;
            break;
          }
        }

        if (!matches) return;

        const value =
          variant.options[
            stage
          ];

        if (value) {
          values.add(value);
        }
      }
    );

    return values;
  };


  /* ==========================================================
     EXACT VARIANT
     ========================================================== */

  const findExactVariant = (
    variants,
    selections
  ) => {
    if (
      !selections.length ||
      selections.some(
        (value) => !value
      )
    ) {
      return null;
    }

    return (
      variants.find(
        (variant) => {
          if (
            variant.options.length <
            selections.length
          ) {
            return false;
          }

          return selections.every(
            (
              value,
              index
            ) =>
              variant.options[
                index
              ] === value
          );
        }
      ) || null
    );
  };


  /* ==========================================================
     SELECT RENDERER

     Important:
     Dawn reads option[selected].

     Therefore .selected alone is NOT enough.
     We keep both DOM state and selected attribute synchronized.
     ========================================================== */

  const renderSelect = (
    field,
    allowedValues,
    selectedValue = ''
  ) => {
    const select =
      getSelect(field);

    if (!select) return;

    const pool =
      ensureFieldPool(field);

    if (
      !pool ||
      pool.type !== 'select'
    ) {
      return;
    }

    const fragment =
      document.createDocumentFragment();

    let renderedCount = 0;

    if (pool.placeholder) {
      const placeholder =
        pool.placeholder.cloneNode(
          true
        );

      const selected =
        !selectedValue;

      placeholder.selected =
        selected;

      placeholder.defaultSelected =
        selected;

      if (selected) {
        placeholder.setAttribute(
          'selected',
          'selected'
        );
      } else {
        placeholder.removeAttribute(
          'selected'
        );
      }

      fragment.appendChild(
        placeholder
      );
    }

    pool.options.forEach(
      (storedOption) => {
        const value =
          String(
            storedOption.value || ''
          );

        if (
          !allowedValues.has(value)
        ) {
          return;
        }

        const option =
          storedOption.cloneNode(
            true
          );

        option.disabled = false;
        option.hidden = false;

        option.removeAttribute(
          'disabled'
        );

        option.removeAttribute(
          'hidden'
        );

        const selected =
          Boolean(
            selectedValue &&
            value === selectedValue
          );

        option.selected =
          selected;

        option.defaultSelected =
          selected;

        if (selected) {
          option.setAttribute(
            'selected',
            'selected'
          );
        } else {
          option.removeAttribute(
            'selected'
          );
        }

        fragment.appendChild(
          option
        );

        renderedCount += 1;
      }
    );

    select.replaceChildren(
      fragment
    );

    if (
      selectedValue &&
      allowedValues.has(
        selectedValue
      )
    ) {
      select.value =
        selectedValue;
    } else {
      select.value = '';
    }

    select.disabled =
      renderedCount === 0;
  };


  /* ==========================================================
     RADIO / PILL RENDERER
     ========================================================== */

  const getRadioLabel = (
    field,
    radio
  ) => {
    if (
      !field ||
      !radio?.id
    ) {
      return null;
    }

    return (
      Array.from(
        field.querySelectorAll(
          'label'
        )
      ).find(
        (label) =>
          label.htmlFor ===
          radio.id
      ) || null
    );
  };

  const renderRadios = (
    field,
    allowedValues,
    selectedValue = ''
  ) => {
    const radios =
      getRadios(field);

    radios.forEach(
      (radio) => {
        const value =
          String(
            radio.value || ''
          );

        const allowed =
          allowedValues.has(value);

        const label =
          getRadioLabel(
            field,
            radio
          );

        radio.disabled =
          !allowed;

        radio.hidden =
          !allowed;

        if (label) {
          label.hidden =
            !allowed;
        }

        const checked =
          Boolean(
            allowed &&
            selectedValue &&
            value === selectedValue
          );

        radio.checked =
          checked;

        if (checked) {
          radio.setAttribute(
            'checked',
            'checked'
          );
        } else {
          radio.removeAttribute(
            'checked'
          );
        }
      }
    );

    const output =
      field.querySelector(
        '[data-selected-value]'
      );

    if (output) {
      output.textContent =
        selectedValue || '';
    }
  };


  const renderField = (
    field,
    allowedValues,
    selectedValue = ''
  ) => {
    if (getSelect(field)) {
      renderSelect(
        field,
        allowedValues,
        selectedValue
      );

      return;
    }

    renderRadios(
      field,
      allowedValues,
      selectedValue
    );
  };


  /* ==========================================================
     ENABLE / DISABLE FIELD
     ========================================================== */

  const setFieldDisabled = (
    field,
    disabled
  ) => {
    const select =
      getSelect(field);

    if (select) {
      select.disabled =
        Boolean(disabled);

      return;
    }

    getRadios(field)
      .forEach(
        (radio) => {
          if (disabled) {
            radio.disabled = true;
          } else if (
            !radio.hidden
          ) {
            radio.disabled = false;
          }
        }
      );
  };


  /* ==========================================================
     PRODUCT FORM
     ========================================================== */

  const getProductFormElement = (
    picker
  ) => {
    const formId =
      picker?.dataset
        ?.productFormId;

    if (formId) {
      const form =
        document.getElementById(
          formId
        );

      if (form) return form;
    }

    return picker
      ?.closest('product-info')
      ?.querySelector(
        'form[action*="/cart/add"]'
      );
  };

  const getProductFormComponent = (
    picker
  ) => {
    const form =
      getProductFormElement(
        picker
      );

    return form?.closest(
      'product-form'
    ) || null;
  };


  /* ==========================================================
     DISABLE PURCHASE WHILE INCOMPLETE
     ========================================================== */

  const disablePurchase = (
    picker
  ) => {
    if (!picker) return;

    picker.classList.add(
      'rd-pdp-v2--incomplete'
    );

    const form =
      getProductFormElement(
        picker
      );

    const variantInput =
      form?.querySelector(
        'input[name="id"]'
      );

    if (variantInput) {
      variantInput.value = '';
    }

    const submit =
      form?.querySelector(
        '[type="submit"][name="add"]'
      );

    if (submit) {
      submit.disabled = true;

      submit.setAttribute(
        'disabled',
        'disabled'
      );

      submit.setAttribute(
        'aria-disabled',
        'true'
      );
    }

    const text =
      submit?.querySelector(
        '.rd-pdp__add-text'
      ) ||
      submit?.querySelector(
        'span'
      );

    if (text) {
      text.textContent = 'Choose Options';
    }

    const productInfo =
      picker.closest(
        'product-info'
      );

    productInfo
      ?.querySelectorAll(
        '[data-rd-sticky-atc-button], [data-rd-sticky-buy-now]'
      )
      .forEach(
        (button) => {
          button.disabled = true;
        }
      );

    const stickyLabel =
      productInfo?.querySelector(
        '[data-rd-sticky-atc-label]'
      );

    if (stickyLabel) {
      stickyLabel.textContent = 'Choose Options';
    }
  };


  /* ==========================================================
     ENABLE PURCHASE

     This is the important fix.

     Once we have an exact AVAILABLE variant:
     - restore hidden variant ID
     - ensure hidden ID input is enabled
     - enable Dawn product-form
     - remove native disabled
     - remove aria-disabled
     - restore Add to Cart text
     ========================================================== */

  const enablePurchase = (
    picker,
    variant
  ) => {
    if (
      !picker ||
      !variant?.id
    ) {
      return;
    }

    picker.classList.remove(
      'rd-pdp-v2--incomplete'
    );

    picker.dataset
      .rdV2ResolvedVariantId =
      String(
        variant.id
      );

    const form =
      getProductFormElement(
        picker
      );

    const productForm =
      getProductFormComponent(
        picker
      );

    const variantInput =
      form?.querySelector(
        'input[name="id"]'
      );

    if (variantInput) {
      variantInput.disabled =
        false;

      variantInput.removeAttribute(
        'disabled'
      );

      variantInput.value =
        String(
          variant.id
        );
    }

    const isAvailable = variant.available !== false;
    const buttonText = isAvailable
      ? (window.variantStrings?.addToCart || 'Add to Cart')
      : (window.variantStrings?.soldOut || 'Sold Out');

    /*
     * Use Dawn's own button method first.
     */

    if (
      productForm &&
      typeof productForm
        .toggleSubmitButton ===
        'function'
    ) {
      productForm
        .toggleSubmitButton(
          !isAvailable,
          buttonText
        );
    }

    const submit =
      form?.querySelector(
        '[type="submit"][name="add"]'
      );

    if (submit) {
      if (isAvailable) {
        submit.disabled = false;

        submit.removeAttribute(
          'disabled'
        );

        submit.removeAttribute(
          'aria-disabled'
        );
      } else {
        submit.disabled = true;

        submit.setAttribute(
          'disabled',
          'disabled'
        );

        submit.setAttribute(
          'aria-disabled',
          'true'
        );
      }

      const text =
        submit.querySelector(
          '.rd-pdp__add-text'
        ) ||
        submit.querySelector(
          'span'
        );

      if (text) {
        text.textContent = buttonText;
      }
    }

    /*
     * Keep Druvient mobile sticky buttons
     * synchronized immediately.
     */

    const productInfo =
      picker.closest(
        'product-info'
      );

    productInfo
      ?.querySelectorAll(
        '[data-rd-sticky-atc-button], [data-rd-sticky-buy-now]'
      )
      .forEach(
        (button) => {
          button.disabled = !isAvailable;
        }
      );

    const stickyLabel =
      productInfo?.querySelector(
        '[data-rd-sticky-atc-label]'
      );

    if (stickyLabel) {
      stickyLabel.textContent = buttonText;
    }

    try {
      if (window.PUB_SUB_EVENTS && typeof window.publish === 'function') {
        window.publish(window.PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId: picker.dataset?.section || '',
            html: null,
            variant: variant
          }
        });
      }
    } catch (e) {}
  };


  /* ==========================================================
     VALIDATE CURRENT SELECTION PATH
     ========================================================== */

  const validateState = (
    state
  ) => {
    let validCompletedIndex =
      -1;

    for (
      let stage = 0;
      stage <=
      state.completedIndex;
      stage += 1
    ) {
      const allowed =
        getAllowedValues(
          state.variants,
          state.selections,
          stage
        );

      const selected =
        state.selections[
          stage
        ];

      if (
        !selected ||
        !allowed.has(
          selected
        )
      ) {
        for (
          let reset = stage;
          reset <
          state.selections.length;
          reset += 1
        ) {
          state.selections[
            reset
          ] = '';
        }

        break;
      }

      validCompletedIndex =
        stage;
    }

    state.completedIndex =
      validCompletedIndex;
  };


  /* ==========================================================
     APPLY CASCADE
     ========================================================== */

  const applyState = (
    picker,
    state
  ) => {
    if (
      !picker ||
      !state
    ) {
      return;
    }

    const fields =
      getFields(picker);

    if (!fields.length) {
      return;
    }

    fields.forEach(
      ensureFieldPool
    );


    /* ----------------------------------------------------------
       NON-CASCADE MODE
       ---------------------------------------------------------- */

    if (
      !isCascade(picker)
    ) {
      fields.forEach(
        (
          field,
          stage
        ) => {
          const allowed =
            new Set();

          state.variants.forEach(
            (variant) => {
              const value =
                variant.options[
                  stage
                ];

              if (value) {
                allowed.add(
                  value
                );
              }
            }
          );

          field.hidden =
            false;

          renderField(
            field,
            allowed,
            state.selections[
              stage
            ] || ''
          );
        }
      );

      return;
    }


    validateState(
      state
    );


    /* ----------------------------------------------------------
       RENDER EACH STAGE
       ---------------------------------------------------------- */

    fields.forEach(
      (
        field,
        stage
      ) => {
        const shouldShow =
          stage <=
          state.completedIndex +
            1;

        field.hidden =
          !shouldShow;

        if (!shouldShow) {
          setFieldDisabled(
            field,
            true
          );

          return;
        }

        const allowedValues =
          getAllowedValues(
            state.variants,
            state.selections,
            stage
          );

        const selectedValue =
          stage <=
          state.completedIndex
            ? state.selections[
                stage
              ]
            : '';

        renderField(
          field,
          allowedValues,
          selectedValue
        );

        setFieldDisabled(
          field,
          allowedValues.size ===
            0
        );
      }
    );


    /* ----------------------------------------------------------
       RESOLVE FINAL VARIANT
       ---------------------------------------------------------- */

    const exactVariant =
      findExactVariant(
        state.variants,
        state.selections
      );

    const isComplete =
      Boolean(
        exactVariant &&
        state.completedIndex >=
          fields.length - 1
      );


    if (isComplete) {
      /*
       * Exact available variant found.
       *
       * Restore the actual variant ID and
       * reactivate Add to Cart.
       */

      enablePurchase(
        picker,
        exactVariant
      );
    } else {
      delete picker.dataset
        .rdV2ResolvedVariantId;

      disablePurchase(
        picker
      );
    }
  };


  /* ==========================================================
     SAFARI-SAFE APPLY
     ========================================================== */

  const scheduleApplyState = (
    picker,
    state
  ) => {
    const existingFrame =
      pendingFrames.get(
        picker
      );

    if (existingFrame) {
      cancelAnimationFrame(
        existingFrame
      );
    }

    const frame =
      requestAnimationFrame(
        () => {
          pendingFrames.delete(
            picker
          );

          if (
            !picker.isConnected
          ) {
            return;
          }

          applyState(
            picker,
            state
          );
        }
      );

    pendingFrames.set(
      picker,
      frame
    );
  };


  /* ==========================================================
     INITIALIZE PICKER
     ========================================================== */

  const initializePicker = (
    picker
  ) => {
    if (!picker) return;

    const fields =
      getFields(picker);

    if (!fields.length) {
      return;
    }

    fields.forEach(
      ensureFieldPool
    );

    const key =
      getStateKey(
        picker
      );

    let state =
      states.get(key);

    const variants =
      parseVariantMap(
        picker
      );

    if (!state) {
      /*
       * Manual-choice initial state.
       *
       * Do NOT hydrate the dropdowns from:
       * - Shopify's selected_or_first_available_variant
       * - ?variant= URL parameters
       * - search query matching
       *
       * A customer must explicitly choose the dropdown values.
       * The in-memory `states` map is intentionally retained so a
       * customer's manual choice survives Dawn DOM replacement on
       * this same product-page interaction. Nothing is persisted in
       * localStorage, sessionStorage, cookies, or across users.
       */
      state = {
        completedIndex: -1,
        selections: new Array(fields.length).fill(''),
        variants
      };

      states.set(
        key,
        state
      );
    } else {
      state.variants =
        variants;

      if (
        state.selections.length !==
        fields.length
      ) {
        state.selections =
          new Array(
            fields.length
          ).fill('');

        state.completedIndex =
          -1;
      }
    }

    applyState(
      picker,
      state
    );
  };


  /* ==========================================================
     CHANGE HANDLER
     ========================================================== */

  const handleChange = (
    event
  ) => {
    const target =
      event.target;

    if (
      !target?.matches?.(
        CONTROL_SELECTOR
      )
    ) {
      return;
    }

    const picker =
      getPicker(target);

    if (
      !picker ||
      !isCascade(picker)
    ) {
      return;
    }

    const field =
      target.closest(
        FIELD_SELECTOR
      );

    if (!field) return;

    const fields =
      getFields(picker);

    const targetIndex =
      fields.indexOf(
        field
      );

    if (
      targetIndex < 0
    ) {
      return;
    }

    const key =
      getStateKey(
        picker
      );

    const state =
      states.get(key);

    if (!state) return;

    const value =
      String(
        target.value || ''
      );


    /* ----------------------------------------------------------
       VERIFY THIS VALUE IS VALID FOR THE CURRENT PARENT PATH
       ---------------------------------------------------------- */

    const currentlyAllowed =
      getAllowedValues(
        state.variants,
        state.selections,
        targetIndex
      );

    if (
      value &&
      !currentlyAllowed.has(
        value
      )
    ) {
      return;
    }


    /* ----------------------------------------------------------
       SAVE CURRENT SELECTION
       ---------------------------------------------------------- */

    state.selections[
      targetIndex
    ] = value;


    /* ----------------------------------------------------------
       PARENT CHANGE INVALIDATES ALL CHILDREN
       ---------------------------------------------------------- */

    for (
      let index =
        targetIndex + 1;
      index <
      state.selections.length;
      index += 1
    ) {
      state.selections[
        index
      ] = '';
    }


    state.completedIndex =
      value
        ? targetIndex
        : targetIndex - 1;


    /*
     * Keep purchase disabled until
     * every required option has been chosen.
     */

    if (
      targetIndex <
      fields.length - 1
    ) {
      disablePurchase(
        picker
      );
    }


    /*
     * Bubble phase + RAF:
     *
     * Safari commits native selection.
     * Dawn receives normal change.
     * Then RD rebuilds dependent fields.
     */

    scheduleApplyState(
      picker,
      state
    );
  };


  document.addEventListener(
    'change',
    handleChange,
    false
  );


  /* ==========================================================
     DAWN DOM REPLACEMENT OBSERVER

     Dawn can replace variant-selects after
     fetching the new product section.
     ========================================================== */

  const observer =
    new MutationObserver(
      (mutations) => {
        mutations.forEach(
          (mutation) => {
            mutation.addedNodes.forEach(
              (node) => {
                if (
                  !(
                    node instanceof
                    Element
                  )
                ) {
                  return;
                }

                if (
                  node.matches(
                    PICKER_SELECTOR
                  )
                ) {
                  initializePicker(
                    node
                  );
                }

                node
                  .querySelectorAll?.(
                    PICKER_SELECTOR
                  )
                  .forEach(
                    initializePicker
                  );
              }
            );
          }
        );
      }
    );

  observer.observe(
    document.documentElement,
    {
      childList: true,
      subtree: true
    }
  );


  /* ==========================================================
     BOOT
     ========================================================== */

  const initAll = (
    root = document
  ) => {
    if (
      root.matches?.(
        PICKER_SELECTOR
      )
    ) {
      initializePicker(
        root
      );
    }

    root
      .querySelectorAll?.(
        PICKER_SELECTOR
      )
      .forEach(
        initializePicker
      );
  };


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initAll(
          document
        );
      },
      {
        once: true
      }
    );
  } else {
    initAll(
      document
    );
  }


  /* ==========================================================
     SHOPIFY THEME EDITOR
     ========================================================== */

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      initAll(
        event.target
      );
    }
  );

  document.addEventListener(
    'shopify:section:unload',
    (event) => {
      event.target
        .querySelectorAll?.(
          PICKER_SELECTOR
        )
        .forEach(
          (picker) => {
            const frame =
              pendingFrames.get(
                picker
              );

            if (frame) {
              cancelAnimationFrame(
                frame
              );

              pendingFrames.delete(
                picker
              );
            }

            states.delete(
              getStateKey(
                picker
              )
            );
          }
        );
    }
  );
})();